import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publicRequest } from '../../requestMethods';
import RoutePlannerResults from './RoutePlannerResults';
import MosqueLoader from '../../utils/MosqueLoader';
import Header from '../Header';
import Footer from '../Footer';

const RoutePlannerPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [route, setRoute] = useState(null);
    const [placesAlongRoute, setPlacesAlongRoute] = useState([]);
    const [startLocation, setStartLocation] = useState(null);
    const [endLocation, setEndLocation] = useState(null);

    useEffect(() => {
        const loadRoute = async () => {
            // Get parameters from URL
            const startName = searchParams.get('startName');
            const startLat = parseFloat(searchParams.get('startLat'));
            const startLon = parseFloat(searchParams.get('startLon'));
            const endName = searchParams.get('endName');
            const endLat = parseFloat(searchParams.get('endLat'));
            const endLon = parseFloat(searchParams.get('endLon'));

            // Validate parameters
            if (!startName || isNaN(startLat) || isNaN(startLon) || 
                !endName || isNaN(endLat) || isNaN(endLon)) {
                setError('Invalid route parameters. Please plan a new route.');
                setLoading(false);
                return;
            }

            setStartLocation({ name: startName, lat: startLat, lon: startLon });
            setEndLocation({ name: endName, lat: endLat, lon: endLon });

            try {
                // Calculate route using OSRM API
                const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${startLon},${startLat};${endLon},${endLat}?overview=full&geometries=geojson`;
                
                const routeResponse = await fetch(osrmUrl);
                const routeData = await routeResponse.json();

                if (routeData.code !== 'Ok' || !routeData.routes || routeData.routes.length === 0) {
                    throw new Error('Could not calculate route. Please try different locations.');
                }

                const routeGeometry = routeData.routes[0].geometry;
                // OSRM returns coordinates as [lon, lat], convert to [lat, lon] for Leaflet
                const routeCoordinates = routeGeometry.coordinates.map(coord => [coord[1], coord[0]]);
                setRoute(routeCoordinates);

                // Fetch all mosques and restaurants
                const [allMosquesResponse, allRestaurantsResponse] = await Promise.all([
                    publicRequest.get('/mosques/get-all-mosques'),
                    publicRequest.get('/restaurants/get-all-restaurants').catch(() => ({ data: [] }))
                ]);
                const allMosques = allMosquesResponse.data;
                const allRestaurants = allRestaurantsResponse.data || [];

                // Find places within 5km of the route
                const mosquesNearRoute = findPlacesAlongRoute(routeCoordinates, allMosques, 'mosque');
                const restaurantsNearRoute = findPlacesAlongRoute(routeCoordinates, allRestaurants, 'restaurant');
                
                // Sort each by distance first
                const sortedMosques = mosquesNearRoute.sort((a, b) => a.distanceFromRoute - b.distanceFromRoute);
                const sortedRestaurants = restaurantsNearRoute.sort((a, b) => a.distanceFromRoute - b.distanceFromRoute);
                
                // Interleave function: prioritize restaurants, start with mosque, then restaurant, then alternate
                const interleavePlaces = (mosques, restaurants) => {
                    const result = [];
                    let mosqueIndex = 0;
                    let restaurantIndex = 0;
                    
                    // First: Mosque (if available)
                    if (mosqueIndex < mosques.length) {
                        result.push(mosques[mosqueIndex++]);
                    }
                    
                    // Second: Restaurant (if available)
                    if (restaurantIndex < restaurants.length) {
                        result.push(restaurants[restaurantIndex++]);
                    }
                    
                    // Then alternate: Restaurant, Mosque, Restaurant, Mosque...
                    while (mosqueIndex < mosques.length || restaurantIndex < restaurants.length) {
                        // Add restaurant first (prioritize restaurants)
                        if (restaurantIndex < restaurants.length) {
                            result.push(restaurants[restaurantIndex++]);
                        }
                        // Then mosque
                        if (mosqueIndex < mosques.length) {
                            result.push(mosques[mosqueIndex++]);
                        }
                    }
                    
                    return result;
                };
                
                const combinedPlaces = interleavePlaces(sortedMosques, sortedRestaurants);
                
                setPlacesAlongRoute(combinedPlaces);

            } catch (err) {
                console.error('Error loading route:', err);
                setError(err.message || 'Error loading route. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadRoute();
    }, [searchParams]);

    // Calculate distance from point to line segment
    const pointToLineDistance = (point, lineStart, lineEnd) => {
        const [px, py] = point;
        const [x1, y1] = lineStart;
        const [x2, y2] = lineEnd;

        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        let param = -1;

        if (lenSq !== 0) {
            param = dot / lenSq;
        }

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = px - xx;
        const dy = py - yy;
        
        // Convert to kilometers (Haversine distance)
        const toRad = (value) => (value * Math.PI) / 180;
        const R = 6371; // Earth's radius in km
        const dLat = toRad(dy);
        const dLon = toRad(dx);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(py)) * Math.cos(toRad(yy)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const findPlacesAlongRoute = (routeCoordinates, places, type) => {
        const MAX_DISTANCE_FROM_ROUTE_KM = 5;
        const placesWithDistance = [];

        places.forEach(place => {
            if (!place.location?.latitude || !place.location?.longitude) {
                return;
            }

            const placePoint = [place.location.latitude, place.location.longitude];
            let minDistance = Infinity;

            // Find minimum distance to any segment of the route
            for (let i = 0; i < routeCoordinates.length - 1; i++) {
                const segmentStart = routeCoordinates[i];
                const segmentEnd = routeCoordinates[i + 1];
                const distance = pointToLineDistance(placePoint, segmentStart, segmentEnd);
                minDistance = Math.min(minDistance, distance);
            }

            if (minDistance <= MAX_DISTANCE_FROM_ROUTE_KM) {
                placesWithDistance.push({
                    ...place,
                    type,
                    distanceFromRoute: minDistance
                });
            }
        });

        // Sort by distance from route (closest first)
        return placesWithDistance.sort((a, b) => a.distanceFromRoute - b.distanceFromRoute);
    };

    const handleReset = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <>
                <Header />
                <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MosqueLoader />
                </div>
                {/* <Footer /> */}
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div style={{ 
                    minHeight: '100vh', 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    padding: '2rem'
                }}>
                    <div style={{ 
                        background: '#fef2f2', 
                        border: '1px solid #fecaca', 
                        borderRadius: '8px',
                        padding: '2rem',
                        maxWidth: '500px',
                        textAlign: 'center'
                    }}>
                        <h2 style={{ color: '#991b1b', marginBottom: '1rem' }}>Error</h2>
                        <p style={{ color: '#991b1b', marginBottom: '1.5rem' }}>{error}</p>
                        <button
                            onClick={handleReset}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #1e5631, #2d7a4a)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: '600'
                            }}
                        >
                            Plan New Route
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!route || !startLocation || !endLocation) {
        return null;
    }

    return (
        <div className="route-planner-page-wrapper">
            <Header />
            <div className="route-planner-page-content">
                <RoutePlannerResults
                    startLocation={startLocation}
                    endLocation={endLocation}
                    route={route}
                    places={placesAlongRoute}
                    onReset={handleReset}
                />
            </div>
            {/* <Footer /> */}
        </div>
    );
};

export default RoutePlannerPage;
